import { BaseDestroy } from 'nera-common/angular';
import {
    ChangeDetectionStrategy,
    Component,
    ViewEncapsulation,
    inject,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { HubConnectionState } from '@microsoft/signalr';
import { TranslocoModule } from '@jsverse/transloco';
import { SignalRService } from 'app/core/services/signalr.service';
import { STATUS_BUCKET_TEXT } from 'app/shared/constants/status-bucket.constants';
import { map, of, switchMap, timer } from 'rxjs';

/**
 * The connection state has to stay lost for this long before the indicator
 * appears: the hub starts out Disconnected on every page load and takes a
 * few hundred milliseconds to negotiate, and a warning that flashes on every
 * load is a warning nobody reads.
 */
const OFFLINE_GRACE_MS = 5_000;

/**
 * C6: the "you are not receiving live updates" indicator only ever existed in
 * the `classic` layout, while `app.config.ts` ships `classy` as the default —
 * so in practice nobody saw it. Combined with C5 that meant staring at a page
 * frozen minutes ago and acting on stale state. It now lives in one shared
 * component driven by `getConnectionState$()` and is used by every layout.
 */
@Component({
    selector: 'connection-status',
    standalone: true,
    imports: [MatButtonModule, MatIconModule, MatTooltipModule, TranslocoModule],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './connection-status.component.html',
})
export class ConnectionStatusComponent extends BaseDestroy {
    private readonly _signalRService = inject(SignalRService);

    protected readonly hubState = HubConnectionState;
    protected readonly warningTextClass = STATUS_BUCKET_TEXT.warning;
    protected readonly state = signal<HubConnectionState>(
        HubConnectionState.Connected
    );

    constructor() {

        super();
        this._signalRService
            .getConnectionState$()
            .pipe(
                switchMap((state) =>
                    state === HubConnectionState.Connected
                        ? of(state)
                        : timer(OFFLINE_GRACE_MS).pipe(map(() => state))
                ),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((state) => this.state.set(state));
    }

    protected reconnect(): void {
        this._signalRService.reconnect().catch(() => undefined);
    }
}
